import { scopedFilterKey } from 'src/app/_helpers/user-filter-scope';
import { StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataEntityCollectionsRepository } from '@angular-ru/ngxs/repositories';
import { Injectable } from '@angular/core';
import { Selector, State, createSelector } from '@ngxs/store';

export interface ListingStateModel {
    owner?: string | null;
    filters: {
        gridStateId: number;
        page: number;
        pageSize: number;
        from: Date | null;
        to: Date | null;
        sortField: string;
        sort: string;
        filters: any; // extra filters
        storesIds: number[];
        locationIds: number[] | null;
        tagIds: number[] | null;
        parentId: number | null;
        groupBy: string | null;
        force: boolean;
        onlyUserModifiedReplenished: boolean | null;
    };
    cache?: { [key: string]: any };
}


// Active listing filters are private to the signed-in account; row caches stay in memory.
@StateRepository()
@State<ListingStateModel>({
    name: 'listing',
    defaults: {
        filters: {
            gridStateId: null,
            page: null,
            pageSize: null,
            from: null,
            to: null,
            sortField: null,
            sort: null,
            filters: {},
            storesIds: [],
            locationIds: [],
            tagIds: [],
            parentId: null,
            groupBy: null,
            force: true,
            onlyUserModifiedReplenished: null
        },
        cache: {}
    }
})

@Injectable()
export class ListingState extends NgxsDataEntityCollectionsRepository<ListingStateModel> {
    constructor() {
        super();
    }

    activateUser(owner: string | null): void {
        if (this.getState().owner === owner) return;
        this.persistFilters();
        this.patchState({ owner: null });
        this.resetFilters();
        let filters = {};
        if (owner) {
            try {
                filters = JSON.parse(localStorage.getItem(scopedFilterKey('listingFilters', owner)) || '{}');
            } catch { }
        }
        this.patchState({ owner, filters: { ...this.getState().filters, ...filters }, cache: {} });
    }

    private persistFilters(): void {
        const state = this.getState();
        if (!state.owner) return;
        try {
            localStorage.setItem(scopedFilterKey('listingFilters', state.owner), JSON.stringify(state.filters));
        } catch { }
    }

    // Set all filters
    setAllFilters(filters: Partial<ListingStateModel['filters']>): void {
        this.patchState({
            filters: {
                ...this.getState().filters,
                ...filters,
            },
        });
        this.persistFilters();
    }

    // Set specific filter
    setFilter<K extends keyof ListingStateModel['filters']>(key: K, value: ListingStateModel['filters'][K]): void {
        this.patchState({
            filters: {
                ...this.getState().filters,
                [key]: value,
            },
        });
        this.persistFilters();
    }

    // Set or update cached data for a given key
    setCacheItem(key: string, value: any): void {
        const current = this.getState();
        const cache = { ...(current.cache || {}) };
        cache[key] = value;
        this.patchState({
            ...current,
            cache
        });
    }

    // Clear cache entirely
    clearCache(): void {
        const current = this.getState();
        this.patchState({
            ...current,
            cache: {}
        });
    }

    // Reset all filters
    resetFilters(): void {
        this.patchState({
            filters: {
                gridStateId: null,
                page: null,
                pageSize: null,
                from: null,
                to: null,
                sortField: null,
                sort: null,
                filters: {},
                storesIds: [],
                locationIds: [],
                tagIds: [],
                parentId: null,
                groupBy: null,
                force: true,
                onlyUserModifiedReplenished: null,
            },
            cache: {}
        });
        this.persistFilters();
    }

    //  Get all filters
    @Selector()
    static getFilters(state: ListingStateModel): ListingStateModel['filters'] {
        return state.filters;
    }

    // Get specific filter
    @Selector()
    static getSpecificFilter<K extends keyof ListingStateModel['filters']>(key: K) {
        return createSelector([ListingState], (state: ListingStateModel) => state.filters[key]);
    }
}
